import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, TextField, MenuItem, Button, Divider, IconButton, Card, CardContent, InputLabel, FormControl, Select, Container, RadioGroup, FormControlLabel, Radio, CircularProgress
} from '@mui/material';
import { ArrowForward, ArrowBack, Save as SaveIcon, CheckCircleOutline, Close as CloseIcon } from '@mui/icons-material';
import BackButton from "./BackButton";

const defaultState = {
    generalObservation: "",
    physicalAssessment: {
        muscleToneUpper: "Normal", muscleToneLower: "Normal",
        equilibrium: "Normal", posturalTone: "Normal",
        atnr: "Normal", stnr: "Normal", tlr: "Normal",
        tightnessUpper: "Not present", tightnessLower: "Not present", deformities: "None",
        voluntaryUpper: "Normal", voluntaryLower: "Normal",
        comment: ""
    },
    positionTransitions: {
        rollingBefore: "Achieved", rollingAfter: "Achieved",
        quadripodBefore: "Achieved", quadripodAfter: "Achieved",
        walkingBefore: "Achieved", walkingAfter: "Achieved",
        jumpingBefore: "Achieved", jumpingAfter: "Achieved",
        standingBefore: "Not Achieved", standingAfter: "Achieved",
        stairsBefore: "Achieved", stairsAfter: "Achieved",
        balanceBeamBefore: "Achieved", balanceBeamAfter: "Achieved",
        catchBallBefore: "Achieved", catchBallAfter: "Achieved",
        throwBallBefore: "Not Achieved", throwBallAfter: "Achieved",
        kickBallBefore: "Achieved", kickBallAfter: "Achieved",
        rideCycleBefore: "Not Achieved", rideCycleAfter: "Achieved",
        comment: ""
    },
    behavioralAttention: {
        commandFollowing: "Good (60%)",
        spanOfReaction: "Good (40%)",
        attention: "Good (50%)",
        hyperactivity: "Reduced (60%)",
        verbalStimming: "Showing improvement with vibrator stimulation",
        comment: ""
    },
    fineMotor: {
        coordination: "Good (50%)",
        unilateral: "Good (50%)",
        bilateral: "Good (50%)",
        eyeHand: "Good (60%)",
        comment: ""
    },
    grossMotor: {
        status: "The child is performing all gross motor activities. According to age, gross motor skills are achieved.",
        comment: ""
    },
    nonPrehensile: {
        pushingBefore: "Not achieved well", pushingAfter: "Achieved",
        pullingBefore: "Achieved", pullingAfter: "Achieved",
        clappingBefore: "Achieved", clappingAfter: "Achieved"
    },
    prehensile: {
        cylindricalRt: "Good", cylindricalLf: "Good", 
        sphericalRt: "Good", sphericalLf: "Good", 
        pincerRt: "Good", pincerLf: "Good", 
        beading: "Good", scissoring: "Not achieved well",
        comment: ""
    },
    writingSkills: {
        pincerGrip: "Developed well",
        fingerGripping: "Good (50%)",
        writingAlphabets: "Able to write alphabets",
        comment: ""
    },
    selfCare: {
        eating: "Eats independently but rejects some rice textures",
        bathing: "Individual bathing can be developed well",
        dressingUpper: "Wears shirts with help",
        dressingLower: "Wears independently",
        toileting: "Achieved well",
        buttoning: "Not achieved",
        comment: ""
    },
    preferences: {
        sensory: "Oral sensory seeking behavior (verbal stimming observed)",
        food: "Picky eater, prefers routine daily food",
        play: "Indoor and outdoor play are normal"
    }
};

const beforeAfterOptions = ["Achieved", "Emerging", "Not Achieved"];
const normalOptions = ["Normal", "Abnormal", "Not Tested"];
const goodOptions = ["Good", "Fair", "Poor", "Developed well", "Achieved well", "Not achieved"];

const DICT = {
    "General Observation": { te: "సాధారణ పరిశీలన" },
    "Physical Assessment": { te: "శారీరక మూల్యాంకనం" },
    "Positions & Transitions": { te: "స్థానాలు & పరివర్తనాలు" },
    "Behavioral & Attention": { te: "ప్రవర్తన & శ్రద్ధ" },
    "Fine Motor Development": { te: "సూక్ష్మ మోటార్ అభివృద్ధి" },
    "Gross Motor Development": { te: "స్థూల మోటార్ అభివృద్ధి" },
    "Hand Function": { te: "చేతి పనితీరు" },
    "Writing Skills": { te: "రాత నైపుణ్యాలు" },
    "Self Care & Daily Living": { te: "స్వీయ జాగ్రత్త & రోజువారీ జీవనం" },
    "Sensory & Food Preferences": { te: "సున్నితత్వ & ఆహార ప్రాధాన్యతలు" },
    "Muscle Tone": { te: "కండరాల స్థితి" },
    "Postural Observation": { te: "భంగిమ పరిశీలన" },
    "Reflex Integration": { te: "రిఫ్లెక్స్ ఇంటిగ్రేషన్" },
    "Tightness & Deformities": { te: "బిగింపు & వైకల్యాలు" },
    "Voluntary Control": { te: "స్వచ్ఛంద నియంత్రణ" },
    "Non-Prehensile Hand Function": { te: "నాన్-ప్రిహెన్సైల్ చేతి పనితీరు" },
    "Prehensile Hand Function": { te: "ప్రిహెన్సైల్ చేతి పనితీరు" },
    "Preferences": { te: "ప్రాధాన్యతలు" }
};

const FormSectionCard = ({ title, children, icon: Icon }) => (
    <Card sx={{ mb: 3, borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}>
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            {Icon && <Icon fontSize="small" />}
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{title}</Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
            {children}
        </CardContent>
    </Card>
);

const OTReassessmentForm = ({ onSubmit, submitting }) => {
    const [step, setStep] = useState(0);
    const [lang, setLang] = useState('en');
    const [data, setData] = useState(defaultState);
    const navigate = useNavigate();

    const lx = (text) => {
        if (lang === 'en') return text;
        return DICT[text] ? (DICT[text].te || text) : text;
    };

    const handleChange = (section, field, value) => {
        if (section) {
            setData({ ...data, [section]: { ...data[section], [field]: value } });
        } else {
            setData({ ...data, [field]: value });
        }
    };

    const handleNext = () => setStep(step + 1);
    const handlePrev = () => setStep(step - 1);

    const handleSubmitLocal = () => {
        if (onSubmit) onSubmit(data);
    };

    const renderInput = (label, section, field, options = null, type = "text") => {
        const val = section ? data[section][field] : data[field];
        return (
            <Box sx={{ mb: 2 }}>
                <InputLabel sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>{lx(label)}</InputLabel>
                {options ? (
                    <Select
                        fullWidth
                        size="small"
                        value={val}
                        onChange={(e) => handleChange(section, field, e.target.value)}
                        sx={{ borderRadius: '12px', bgcolor: '#f8fafc' }}
                    >
                        {options.map(opt => <MenuItem key={opt} value={opt}>{lx(opt)}</MenuItem>)}
                    </Select>
                ) : (
                    <TextField
                        fullWidth
                        size="small"
                        type={type}
                        multiline={type === "textarea"}
                        rows={type === "textarea" ? 3 : 1}
                        value={val}
                        onChange={(e) => handleChange(section, field, e.target.value)}
                        placeholder="Type observation..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}
                    />
                )}
            </Box>
        );
    };

    const renderBeforeAfterRow = (label, section, beforeField, afterField, options = beforeAfterOptions) => (
        <Box sx={{ mb: 2, p: 2, borderRadius: '16px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.dark' }}>{lx(label)}</Typography>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <InputLabel sx={{ mb: 0.5, fontSize: '0.75rem', fontWeight: 'bold', color: 'text.secondary' }}>BEFORE</InputLabel>
                    <Select
                        fullWidth
                        size="small"
                        value={data[section][beforeField]}
                        onChange={(e) => handleChange(section, beforeField, e.target.value)}
                        sx={{ borderRadius: '8px', bgcolor: 'white' }}
                    >
                        {options.map(opt => <MenuItem key={opt} value={opt}>{lx(opt)}</MenuItem>)}
                    </Select>
                </Grid>
                <Grid item xs={6}>
                    <InputLabel sx={{ mb: 0.5, fontSize: '0.75rem', fontWeight: 'bold', color: 'success.main' }}>AFTER</InputLabel>
                    <Select
                        fullWidth
                        size="small"
                        value={data[section][afterField]}
                        onChange={(e) => handleChange(section, afterField, e.target.value)}
                        sx={{ borderRadius: '8px', bgcolor: 'white' }}
                    >
                        {options.map(opt => <MenuItem key={opt} value={opt}>{lx(opt)}</MenuItem>)}
                    </Select>
                </Grid>
            </Grid>
        </Box>
    );

    const steps = [
        {
            title: lx("Physical Assessment"),
            content: (
                <>
                    <FormSectionCard title={lx("General Observation")}>
                        {renderInput("General Observation", null, "generalObservation", null, "textarea")}
                    </FormSectionCard>

                    <FormSectionCard title={lx("Muscle Tone")}>
                        <Grid container spacing={3}>
                            <Grid item xs={6}>{renderInput("Upper Limb", "physicalAssessment", "muscleToneUpper", normalOptions)}</Grid>
                            <Grid item xs={6}>{renderInput("Lower Limb", "physicalAssessment", "muscleToneLower", normalOptions)}</Grid>
                        </Grid>
                    </FormSectionCard>

                    <FormSectionCard title={lx("Postural Observation")}>
                        <Grid container spacing={3}>
                            <Grid item xs={6}>{renderInput("Equilibrium", "physicalAssessment", "equilibrium", normalOptions)}</Grid>
                            <Grid item xs={6}>{renderInput("Postural Tone", "physicalAssessment", "posturalTone", normalOptions)}</Grid>
                        </Grid>
                    </FormSectionCard>

                    <FormSectionCard title={lx("Reflex Integration")}>
                        <Grid container spacing={4}>
                            <Grid item xs={4}>{renderInput("ATNR", "physicalAssessment", "atnr", normalOptions)}</Grid>
                            <Grid item xs={4}>{renderInput("STNR", "physicalAssessment", "stnr", normalOptions)}</Grid>
                            <Grid item xs={4}>{renderInput("TLR", "physicalAssessment", "tlr", normalOptions)}</Grid>
                        </Grid>
                    </FormSectionCard>

                    <FormSectionCard title={lx("Tightness & Deformities")}>
                        {renderInput("Upper Limb Tightness", "physicalAssessment", "tightnessUpper")}
                        {renderInput("Lower Limb Tightness", "physicalAssessment", "tightnessLower")}
                        {renderInput("Deformities", "physicalAssessment", "deformities")}
                    </FormSectionCard>

                    <FormSectionCard title={lx("Voluntary Control")}>
                        <Grid container spacing={3}>
                            <Grid item xs={6}>{renderInput("Upper Limb", "physicalAssessment", "voluntaryUpper", normalOptions)}</Grid>
                            <Grid item xs={6}>{renderInput("Lower Limb", "physicalAssessment", "voluntaryLower", normalOptions)}</Grid>
                        </Grid>
                    </FormSectionCard>
                </>
            )
        },
        {
            title: lx("Positions & Transitions"),
            content: (
                <FormSectionCard title={lx("Positions & Transitions")}>
                    {[
                        { key: "rolling", label: "Rolling" },
                        { key: "quadripod", label: "Able to assume Quadruped position" },
                        { key: "walking", label: "Walking & Running" },
                        { key: "jumping", label: "Jumping" },
                        { key: "standing", label: "Standing on one leg" },
                        { key: "stairs", label: "Climb up & down stairs" },
                        { key: "balanceBeam", label: "Walk on balance beam" },
                        { key: "catchBall", label: "Catch ball" },
                        { key: "throwBall", label: "Throw ball over head" },
                        { key: "kickBall", label: "Kick ball" },
                        { key: "rideCycle", label: "Ride cycle" }
                    ].map(f => (
                         <Box key={f.key}>
                            {renderBeforeAfterRow(f.label, "positionTransitions", `${f.key}Before`, `${f.key}After`)}
                         </Box>
                    ))}
                </FormSectionCard>
            )
        },
        {
            title: lx("Behavioral & Attention"),
            content: (
                <FormSectionCard title={lx("Behavioral & Attention")}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>{renderInput("Command following", "behavioralAttention", "commandFollowing")}</Grid>
                        <Grid item xs={12}>{renderInput("Span of reaction", "behavioralAttention", "spanOfReaction")}</Grid>
                        <Grid item xs={12}>{renderInput("Attention", "behavioralAttention", "attention")}</Grid>
                        <Grid item xs={12}>{renderInput("Hyperactivity", "behavioralAttention", "hyperactivity")}</Grid>
                        <Grid item xs={12}>{renderInput("Verbal stimming", "behavioralAttention", "verbalStimming")}</Grid>
                    </Grid>
                </FormSectionCard>
            )
        },
        {
            title: lx("Fine & Gross Motor"),
            content: (
                <>
                    <FormSectionCard title={lx("Fine Motor Development")}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>{renderInput("Coordination (Hand to Hand)", "fineMotor", "coordination")}</Grid>
                            <Grid item xs={12}>{renderInput("Unilateral", "fineMotor", "unilateral")}</Grid>
                            <Grid item xs={12}>{renderInput("Bilateral", "fineMotor", "bilateral")}</Grid>
                            <Grid item xs={12}>{renderInput("Eye-Hand Coordination", "fineMotor", "eyeHand")}</Grid>
                        </Grid>
                    </FormSectionCard>
                    <FormSectionCard title={lx("Gross Motor Development")}>
                        {renderInput("Gross Motor Status", "grossMotor", "status", null, "textarea")}
                    </FormSectionCard>
                </>
            )
        },
        {
            title: lx("Hand Function"),
            content: (
                <>
                    <FormSectionCard title={lx("Non-Prehensile Hand Function")}>
                        {renderBeforeAfterRow("Pushing", "nonPrehensile", "pushingBefore", "pushingAfter")}
                        {renderBeforeAfterRow("Pulling", "nonPrehensile", "pullingBefore", "pullingAfter")}
                        {renderBeforeAfterRow("Clapping", "nonPrehensile", "clappingBefore", "clappingAfter")}
                    </FormSectionCard>
                    
                    <FormSectionCard title={lx("Prehensile Hand Function")}>
                        <Grid container spacing={2}>
                        {["cylindrical", "spherical", "pincer"].map(f => (
                            <Grid item xs={12} key={f}>
                                <Box sx={{ mb: 1, p: { xs: 1.5, sm: 2 }, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                                    <Typography sx={{ minWidth: 150, fontWeight: 600, textTransform: 'capitalize' }}>{f} grasp</Typography>
                                    <Box sx={{ width: { xs: '100%', sm: 'auto' }, flexGrow: 1 }}>{renderInput("Right", "prehensile", `${f}Rt`, goodOptions)}</Box>
                                    <Box sx={{ width: { xs: '100%', sm: 'auto' }, flexGrow: 1 }}>{renderInput("Left", "prehensile", `${f}Lf`, goodOptions)}</Box>
                                </Box>
                            </Grid>
                        ))}
                        </Grid>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={6}>{renderInput("Beading", "prehensile", "beading", goodOptions)}</Grid>
                            <Grid item xs={6}>{renderInput("Scissoring skill", "prehensile", "scissoring")}</Grid>
                        </Grid>
                    </FormSectionCard>
                </>
            )
        },
        {
            title: lx("Writing & Self Care"),
            content: (
                <>
                    <FormSectionCard title={lx("Writing Skills")}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>{renderInput("Pincer grip", "writingSkills", "pincerGrip")}</Grid>
                            <Grid item xs={12}>{renderInput("Finger gripping", "writingSkills", "fingerGripping")}</Grid>
                            <Grid item xs={12}>{renderInput("Writing alphabets", "writingSkills", "writingAlphabets")}</Grid>
                        </Grid>
                    </FormSectionCard>
                    <FormSectionCard title={lx("Self Care & Daily Living")}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>{renderInput("Eating", "selfCare", "eating")}</Grid>
                            <Grid item xs={12}>{renderInput("Bathing", "selfCare", "bathing")}</Grid>
                            <Grid item xs={6}>{renderInput("Dressing (Upper Body)", "selfCare", "dressingUpper")}</Grid>
                            <Grid item xs={6}>{renderInput("Dressing (Lower Body)", "selfCare", "dressingLower")}</Grid>
                            <Grid item xs={6}>{renderInput("Toileting", "selfCare", "toileting")}</Grid>
                            <Grid item xs={6}>{renderInput("Buttoning", "selfCare", "buttoning")}</Grid>
                        </Grid>
                    </FormSectionCard>
                </>
            )
        },
        {
            title: lx("Preferences"),
            content: (
                <FormSectionCard title={lx("Preferences")}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>{renderInput("Sensory Preferences / Avoidances", "preferences", "sensory", null, "textarea")}</Grid>
                        <Grid item xs={12}>{renderInput("Food Preferences / Avoidances", "preferences", "food", null, "textarea")}</Grid>
                        <Grid item xs={12}>{renderInput("Play / Activity Preferences", "preferences", "play", null, "textarea")}</Grid>
                    </Grid>
                </FormSectionCard>
            )
        }
    ];

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                        {lx("Occupational Therapy Reassessment")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {lx("Step")} {step + 1} {lx("of")} {steps.length}: {steps[step].title}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                        size="small"
                        variant={lang === 'en' ? 'contained' : 'outlined'} 
                        onClick={() => setLang('en')}
                        sx={{ borderRadius: '20px' }}
                    >EN</Button>
                    <Button 
                        size="small"
                        variant={lang === 'te' ? 'contained' : 'outlined'} 
                        onClick={() => setLang('te')}
                        sx={{ borderRadius: '20px' }}
                    >తెలుగు</Button>
                </Box>
            </Box>

            <Box sx={{ mb: 4 }}>
                <Grid container spacing={1}>
                    {steps.map((s, idx) => (
                        <Grid item xs key={idx}>
                            <Box sx={{ 
                                height: 4, 
                                borderRadius: 2, 
                                bgcolor: idx <= step ? 'primary.main' : 'divider',
                                transition: 'all 0.3s'
                            }} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ minHeight: '400px' }}>
                {steps[step].content}
            </Box>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 8 }}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={handlePrev}
                    disabled={step === 0}
                    sx={{ borderRadius: '12px', px: 3 }}
                >
                    {lx("Previous Step")}
                </Button>

                {step < steps.length - 1 ? (
                    <Button
                        size="small"
                        variant="contained"
                        endIcon={<ArrowForward />}
                        onClick={handleNext}
                        sx={{ borderRadius: '12px', px: 4 }}
                    >
                        {lx("Continue")}
                    </Button>
                ) : (
                    <Button
                        disabled={submitting}
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={handleSubmitLocal}
                        sx={{ borderRadius: '12px', px: 4 }}
                    >
                        {submitting ? lx("Saving...") : lx("Save Reassessment to Profile")}
                    </Button>
                )}
            </Box>
        </Container>
    );
};

export default OTReassessmentForm;
